import { createStyles, makeStyles, type Theme } from '@material-ui/core/styles';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { Colors } from '@rentguru/commons-utils';
import clsx from 'clsx';
import React, { forwardRef } from 'react';

import { Button } from '../basics';
import { FlatButtonProps } from '../headers';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1),
      cursor: 'pointer',
      display: 'inline-flex',
      justifyContent: 'flex-start',
      flexDirection: 'inherit',
      alignItems: 'center',
      fontFamily: 'Mulish',
      fontWeight: 'normal',
      fontSize: '12px',
      lineHeight: '15px',
      color: Colors.BLUE_GREY,
      '&:hover': {
        color: Colors.SILVER,
      },
      '&:focus': {
        color: Colors.BLUE_GREY,
      },
    },
    active: {
      color: Colors.BLUE_GREY,
      '& $icon': {
        opacity: 1,
      },
    },
    icon: {
      height: 16,
      marginRight: 4,
      marginLeft: 4,
      opacity: 0,
      transition: theme.transitions.create(['opacity', 'transform'], {
        duration: theme.transitions.duration.shorter,
      }),
      userSelect: 'none',
      width: 16,
    },
  })
);

const rotatingIconStyle = (isOpen: boolean): object => ({
  transform: `rotate(${isOpen ? 180 : 0}deg)`,
});

// eslint-disable-next-line react/display-name
export const FlatButton: React.FC<FlatButtonProps> = forwardRef<HTMLButtonElement, FlatButtonProps>((props, ref) => {
  const classes = useStyles();
  const { active, children, hideExpandIcon, isOpen = false, selected, disabled, ...other } = props;

  return (
    <Button
      className={clsx(classes.root, { [classes.active]: active })}
      component="span"
      disableRipple
      disabled={disabled}
      ref={ref}
      {...other}
      style={{ fontWeight: selected ? 'bold' : 'normal' }}
    >
      {children}
      {hideExpandIcon || !active ? null : (
        <ExpandMore
          style={{ ...rotatingIconStyle(isOpen), color: disabled ? Colors.SILVER : undefined }}
          className={clsx(classes.icon)}
        />
      )}
    </Button>
  );
});

FlatButton.defaultProps = {
  active: true,
  hideExpandIcon: false,
  isOpen: false,
  selected: false,
};
